/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../shared/modals/Modal';
import {
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { OperationalFilterPickerRow } from '../../../shared/filters/OperationalFilterPrimitives';
import { operationalFilterStyles } from '../../../shared/filters/operationalFilters.styles';
import { spacing } from '../../../shared/theme/spacing'
import { ticketColors } from '../../../shared/theme/ticketColors'
import { typography } from '../../../shared/theme/typography';
import { matchesSearchText } from '../../../shared/format/searchText';
import { settingsStyles } from '../../../settings/screens/settingsScreen.styles';

const SEARCH_THRESHOLD = 6;

export type InviteBranchOption = { id: string; name: string };

type Props = {
  branches: InviteBranchOption[];
  selectedIds: Set<string>;
  onChange: (next: Set<string>) => void;
  layoutWide: boolean;
};

export function InviteBranchPicker({ branches, selectedIds, onChange, layoutWide }: Props) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<string[]>([]);
  const insets = useSafeAreaInsets();
  const { height: screenHeight } = useWindowDimensions();

  const sorted = useMemo(
    () => [...branches].sort((a, b) => a.name.localeCompare(b.name)),
    [branches]
  );

  const filtered = useMemo(() => {
    if (!query.trim()) return sorted;
    return sorted.filter((b) => matchesSearchText(b.name, query));
  }, [query, sorted]);

  useEffect(() => {
    if (sheetOpen) {
      setDraft([...selectedIds]);
      setQuery('');
    }
  }, [sheetOpen, selectedIds]);

  const toggleBranch = (id: string) => {
    onChange(
      (() => {
        const next = new Set(selectedIds);
        if (next.has(id)) {
          if (next.size <= 1) return next;
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      })()
    );
  };

  const selectAll = () => onChange(new Set(sorted.map((b) => b.id)));
  const clearToFirst = () => {
    const first = sorted[0]?.id;
    if (first) onChange(new Set([first]));
  };

  const valueLabel =
    selectedIds.size === 0
      ? 'Elegí sucursales'
      : selectedIds.size === 1
        ? sorted.find((b) => selectedIds.has(b.id))?.name ?? '1 sucursal'
        : `${selectedIds.size} sucursales`;

  const toggleDraft = (id: string) => {
    setDraft((prev) => {
      if (prev.includes(id)) {
        if (prev.length <= 1) return prev;
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
  };

  const applyDraft = () => {
    if (draft.length === 0) return;
    onChange(new Set(draft));
    setSheetOpen(false);
  };

  if (layoutWide) {
    return (
      <View style={styles.wideWrap} testID="perfil.team.invite.branches">
        {sorted.length >= SEARCH_THRESHOLD ? (
          <TextInput
            value={query}
            onChangeText={setQuery}
            placeholder="Buscar sucursal…"
            placeholderTextColor={ticketColors.ink3}
            style={styles.searchInput}
            accessibilityLabel="Buscar sucursal"
          />
        ) : null}
        <View style={styles.bulkRow}>
          <TouchableOpacity
            style={operationalFilterStyles.chip}
            onPress={selectAll}
            accessibilityLabel="Seleccionar todas las sucursales"
          >
            <Text style={operationalFilterStyles.chipText}>Todas</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={operationalFilterStyles.chip}
            onPress={clearToFirst}
            accessibilityLabel="Dejar solo la primera sucursal"
          >
            <Text style={operationalFilterStyles.chipText}>Solo una</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.checklist}>
          {filtered.map((branch) => {
            const selected = selectedIds.has(branch.id);
            return (
              <TouchableOpacity
                key={branch.id}
                style={[styles.checkRow, selected && styles.checkRowOn]}
                onPress={() => toggleBranch(branch.id)}
                accessibilityRole="checkbox"
                accessibilityState={{ checked: selected }}
                accessibilityLabel={`Sucursal ${branch.name}`}
              >
                <View style={[styles.checkBox, selected && styles.checkBoxOn]}>
                  {selected ? <Text style={styles.checkMark}>✓</Text> : null}
                </View>
                <Text style={styles.checkLabel}>{branch.name}</Text>
              </TouchableOpacity>
            );
          })}
          {filtered.length === 0 ? (
            <Text style={styles.emptyHint}>Ninguna sucursal coincide con la búsqueda.</Text>
          ) : null}
        </View>
      </View>
    );
  }

  return (
    <View testID="perfil.team.invite.branches">
      <OperationalFilterPickerRow
        label="Sucursales"
        valueLabel={valueLabel}
        onPress={() => setSheetOpen(true)}
        active={selectedIds.size > 0}
        accessibilityLabel="Elegir sucursales para la invitación"
      />
      <Modal visible={sheetOpen} transparent animationType="fade" onRequestClose={() => setSheetOpen(false)}>
        <View style={styles.sheetBackdrop}>
          <TouchableOpacity style={StyleSheet.absoluteFill} activeOpacity={1} onPress={() => setSheetOpen(false)} />
          <View
            style={[
              styles.sheetCard,
              { maxHeight: Math.min(520, screenHeight * 0.88), paddingBottom: Math.max(insets.bottom, spacing.sm) },
            ]}
          >
            <View style={styles.sheetHeader}>
              <Text style={styles.sheetTitle}>Sucursales</Text>
              <TouchableOpacity onPress={() => setSheetOpen(false)} accessibilityLabel="Cerrar">
                <Text style={styles.sheetClose}>×</Text>
              </TouchableOpacity>
            </View>
            {sorted.length >= SEARCH_THRESHOLD ? (
              <TextInput
                value={query}
                onChangeText={setQuery}
                placeholder="Buscar…"
                placeholderTextColor={ticketColors.ink3}
                style={styles.searchInput}
              />
            ) : null}
            <View style={styles.bulkRow}>
              <TouchableOpacity style={operationalFilterStyles.chip} onPress={() => setDraft(sorted.map((b) => b.id))}>
                <Text style={operationalFilterStyles.chipText}>Todas</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.sheetList}>
              {filtered.map((branch) => {
                const on = draft.includes(branch.id);
                return (
                  <TouchableOpacity
                    key={branch.id}
                    style={[styles.checkRow, on && styles.checkRowOn]}
                    onPress={() => toggleDraft(branch.id)}
                    accessibilityRole="checkbox"
                    accessibilityState={{ checked: on }}
                  >
                    <View style={[styles.checkBox, on && styles.checkBoxOn]}>
                      {on ? <Text style={styles.checkMark}>✓</Text> : null}
                    </View>
                    <Text style={styles.checkLabel}>{branch.name}</Text>
                  </TouchableOpacity>
                );
              })}
            </ScrollView>
            <TouchableOpacity
              style={[settingsStyles.primaryButton, draft.length === 0 && settingsStyles.disabled]}
              onPress={applyDraft}
              disabled={draft.length === 0}
            >
              <Text style={settingsStyles.primaryButtonText}>Listo</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>
    </View>
  );
}

const styles = StyleSheet.create({
  wideWrap: {
    gap: spacing.sm,
  },
  searchInput: {
    ...typography.body,
    borderWidth: 1,
    borderColor: ticketColors.border,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    color: ticketColors.ink,
    backgroundColor: ticketColors.paper,
  },
  bulkRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  checklist: {
    gap: spacing.xs,
  },
  checkRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: ticketColors.border,
    backgroundColor: ticketColors.paper,
  },
  checkRowOn: {
    borderColor: ticketColors.gold,
  },
  checkBox: {
    width: 22,
    height: 22,
    borderRadius: 4,
    borderWidth: 1.5,
    borderColor: ticketColors.ink3,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkBoxOn: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
  },
  checkMark: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '700',
  },
  checkLabel: {
    ...typography.body,
    color: ticketColors.ink,
    flex: 1,
  },
  emptyHint: {
    ...typography.caption,
    color: ticketColors.ink2,
  },
  sheetBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.45)',
    justifyContent: 'flex-end',
  },
  sheetCard: {
    backgroundColor: ticketColors.bg,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    padding: spacing.lg,
    gap: spacing.md,
  },
  sheetHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  sheetTitle: {
    ...typography.title3,
    color: ticketColors.ink,
  },
  sheetClose: {
    fontSize: 28,
    lineHeight: 28,
    color: ticketColors.ink2,
    paddingHorizontal: spacing.xs,
  },
  sheetList: {
    maxHeight: 320,
  },
});
